import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Modal from '../../components/UI/Modal'
import { apiService } from '../../services/api'

const QueueDetail = () => {
	const { queueId } = useParams()
	const navigate = useNavigate()
	const [queue, setQueue] = useState(null)
	const [connections, setConnections] = useState([])
	const [queries, setQueries] = useState([])
	const [schedules, setSchedules] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [showQueryModal, setShowQueryModal] = useState(false)
	const [showScheduleModal, setShowScheduleModal] = useState(false)
	const [editingQuery, setEditingQuery] = useState(null)
	const [queryForm, setQueryForm] = useState({
		name: '',
		sql: '',
		tableName: '',
		connectionId: '',
		position: 1,
		description: '',
	})
	const [scheduleForm, setScheduleForm] = useState({
		intervalType: 'hour',
		intervalValue: 1,
		pushNow: true,
		startAt: '',
	})

	useEffect(() => {
		if (!queueId || isNaN(parseInt(queueId))) {
			console.error('Invalid queueId detected, redirecting:', queueId)
			navigate('/queues')
			return
		}

		loadQueueData()
	}, [queueId, navigate])

	const loadQueueData = async () => {
		try {
			setLoading(true)
			setError('')

			// Преобразуем queueId в число
			const numericQueueId = parseInt(queueId)
			if (isNaN(numericQueueId)) {
				throw new Error(`Invalid queue ID: ${queueId}`)
			}

			// Загружаем подключения для выпадающего списка
			const connectionsData = await apiService.getConnections()
			setConnections(connectionsData)

			// Загружаем данные очереди (цепочки)
			const queueData = await apiService.getChain(numericQueueId)
			console.log('Loaded queue data:', queueData)
			setQueue(queueData)

			// Загружаем запросы очереди
			const queriesData = await apiService.getChainQueries(numericQueueId)
			setQueries(queriesData.sort((a, b) => a.position - b.position))

			// Загружаем расписания очереди
			const schedulesData = await apiService.getChainSchedules(numericQueueId)
			setSchedules(schedulesData)
		} catch (err) {
			console.error('Failed to load queue data:', err)
			setError('Ошибка при загрузке данных очереди')

			if (err.message.includes('404') || err.message.includes('500')) {
				setTimeout(() => {
					navigate('/queues')
				}, 3000)
			}
		} finally {
			setLoading(false)
		}
	}

	const handleAddQuery = () => {
		setEditingQuery(null)
		setQueryForm({
			name: '',
			sql: '',
			tableName: '',
			connectionId: connections.length > 0 ? connections[0].id : '',
			position: queries.length + 1,
			description: '',
		})
		setShowQueryModal(true)
	}

	const handleEditQuery = query => {
		setEditingQuery(query)
		setQueryForm({
			name: query.name,
			sql: query.sql,
			tableName: query.tableName,
			connectionId: query.connectionId,
			position: query.position,
			description: query.description || '',
		})
		setShowQueryModal(true)
	}

	const handleSaveQuery = async e => {
		e.preventDefault()

		try {
			setError('')

			const numericQueueId = parseInt(queueId)
			if (isNaN(numericQueueId)) {
				throw new Error('Invalid queue ID')
			}

			if (editingQuery) {
				await apiService.updateQuery(editingQuery.id, queryForm, numericQueueId)
			} else {
				await apiService.createQuery(queryForm, numericQueueId)
			}

			await loadQueueData()
			setShowQueryModal(false)
			setEditingQuery(null)
		} catch (err) {
			setError('Ошибка при сохранении запроса')
			console.error('Failed to save query:', err)
		}
	}

	const handleDeleteQuery = async queryId => {
		if (!window.confirm('Вы уверены, что хотите удалить этот запрос?')) {
			return
		}

		try {
			await apiService.deleteQuery(queryId)
			await loadQueueData()
		} catch (err) {
			setError('Ошибка при удалении запроса')
			console.error('Failed to delete query:', err)
		}
	}

	const handleRunQueue = async () => {
		try {
			const numericQueueId = parseInt(queueId)
			if (isNaN(numericQueueId)) {
				throw new Error('Invalid queue ID')
			}

			await apiService.runChain(numericQueueId)
			alert(`Очередь ${queueId} запущена`)
		} catch (err) {
			setError('Ошибка при запуске очереди')
			console.error('Failed to run queue:', err)
		}
	}

	const handleAddSchedule = async e => {
		e.preventDefault()

		try {
			setError('')

			const numericQueueId = parseInt(queueId)
			if (isNaN(numericQueueId)) {
				throw new Error('Invalid queue ID')
			}

			// Форматируем дату в ISO 8601 с часовым поясом UTC+3
			const startAt = scheduleForm.startAt
				? new Date(scheduleForm.startAt).toISOString().replace('Z', '+03:00')
				: new Date().toISOString().replace('Z', '+03:00')

			await apiService.createSchedule(
				{
					...scheduleForm,
					startAt,
				},
				numericQueueId,
			)

			await loadQueueData()
			setShowScheduleModal(false)
			setScheduleForm({
				intervalType: 'hour',
				intervalValue: 1,
				pushNow: true,
				startAt: '',
			})
		} catch (err) {
			setError('Ошибка при сохранении расписания')
			console.error('Failed to save schedule:', err)
		}
	}

	const handleDeleteSchedule = async scheduleId => {
		if (!window.confirm('Вы уверены, что хотите удалить это расписание?')) {
			return
		}

		try {
			await apiService.deleteSchedule(scheduleId)
			await loadQueueData()
		} catch (err) {
			setError('Ошибка при удалении расписания')
			console.error('Failed to delete schedule:', err)
		}
	}

	const getConnectionName = connectionId => {
		if (!connectionId) return 'Не указано'

		const connection = connections.find(conn => {
			// Сравниваем как числа, если возможно
			const connIdNum = parseInt(conn.id)
			const targetIdNum = parseInt(connectionId)

			if (!isNaN(connIdNum) && !isNaN(targetIdNum)) {
				return connIdNum === targetIdNum
			}

			// Если не числа, сравниваем как строки
			return conn.id.toString() === connectionId.toString()
		})

		return connection
			? `${connection.name} (${connection.db_type})`
			: `ID: ${connectionId}`
	}

	const formatDateTime = dateString => {
		if (!dateString) return 'Не указано'
		try {
			const date = new Date(dateString)
			return date.toLocaleString('ru-RU', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
			})
		} catch (err) {
			return 'Неверный формат даты'
		}
	}

	if (loading) {
		return (
			<div>
				<h2>Загрузка данных очереди...</h2>
				<div
					className='loading'
					style={{
						padding: '2rem',
						textAlign: 'center',
						color: '#666',
					}}
				>
					Загрузка данных очереди #{queueId}...
				</div>
			</div>
		)
	}

	if (!queue) {
		return (
			<div>
				<h2>Очередь не найдена</h2>
				<Link to='/queues'>
					<button className='button button-secondary'>
						← Вернуться к очередям
					</button>
				</Link>
			</div>
		)
	}

	return (
		<div>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '2rem',
				}}
			>
				<div>
					<h2>Очередь №{queue.id || queueId}</h2>
					<h3 style={{ color: '#666', marginTop: '0.5rem' }}>
						{queue.name || 'Без названия'}
						{queue.connectionId && (
							<span
								style={{
									marginLeft: '1rem',
									fontSize: '0.8em',
									backgroundColor: '#e8f4fd',
									padding: '4px 8px',
									borderRadius: '4px',
									color: '#0366d6',
								}}
							>
								{getConnectionName(queue.connectionId)}
							</span>
						)}
					</h3>
					{queue.description && (
						<p style={{ color: '#666', marginTop: '0.5rem' }}>
							{queue.description}
						</p>
					)}
				</div>
				<Link to='/queues'>
					<button className='button button-secondary'>
						← Назад к очередям
					</button>
				</Link>
			</div>

			{error && (
				<div
					className='error-message'
					style={{
						backgroundColor: '#fff5f5',
						border: '1px solid #feb2b2',
						color: '#c53030',
						padding: '1rem',
						borderRadius: '8px',
						marginBottom: '1.5rem',
					}}
				>
					{error}
					<button
						onClick={loadQueueData}
						style={{ marginLeft: '1rem' }}
						className='button button-secondary'
					>
						Повторить
					</button>
				</div>
			)}

			{/* Секция расписаний */}
			{schedules.length > 0 && (
				<div style={{ marginBottom: '2rem' }}>
					<h3>Расписания запуска ({schedules.length})</h3>
					<div
						className='table'
						style={{
							width: '100%',
							overflowX: 'auto',
							border: '1px solid #e0e0e0',
							borderRadius: '8px',
							marginBottom: '1rem',
						}}
					>
						<div
							className='table__row table__row_header'
							style={{
								display: 'grid',
								gridTemplateColumns: '1fr 1fr 1fr 1fr',
								backgroundColor: '#f5f5f5',
								fontWeight: '600',
							}}
						>
							<div style={{ padding: '12px 16px' }}>Интервал</div>
							<div style={{ padding: '12px 16px' }}>Запуск сейчас</div>
							<div style={{ padding: '12px 16px' }}>Начало</div>
							<div style={{ padding: '12px 16px' }}>Действия</div>
						</div>
						{schedules.map(schedule => (
							<div
								key={schedule.id}
								className='table__row'
								style={{
									display: 'grid',
									gridTemplateColumns: '1fr 1fr 1fr 1fr',
									borderTop: '1px solid #f0f0f0',
									alignItems: 'center',
								}}
							>
								<div style={{ padding: '12px 16px' }}>
									Каждые {schedule.intervalValue}{' '}
									{schedule.intervalType === 'hour' ? 'час(а)' : 'день(дней)'}
								</div>
								<div style={{ padding: '12px 16px' }}>
									{schedule.pushNow ? 'Да' : 'Нет'}
								</div>
								<div style={{ padding: '12px 16px' }}>
									{formatDateTime(schedule.startAt)}
								</div>
								<div style={{ padding: '12px 16px' }}>
									<button
										className='button button-danger button-small'
										onClick={() => handleDeleteSchedule(schedule.id)}
										style={{ padding: '6px 12px', fontSize: '0.85em' }}
									>
										Удалить
									</button>
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Секция запросов */}
			<h3>Запросы ({queries.length})</h3>

			{queries.length === 0 ? (
				<div className='card' style={{ textAlign: 'center', padding: '3rem' }}>
					<h3>Нет запросов</h3>
					<p style={{ color: '#666', marginBottom: '1.5rem' }}>
						Добавьте первый запрос в очередь
					</p>
					<button className='button' onClick={handleAddQuery}>
						Добавить запрос
					</button>
				</div>
			) : (
				queries.map(query => (
					<div
						key={query.id}
						className='card query-card'
						style={{ marginBottom: '1.5rem' }}
					>
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'flex-start',
							}}
						>
							<div>
								<h4 style={{ marginBottom: '0.5rem' }}>{query.name}</h4>
								{query.description && (
									<p style={{ color: '#666', marginBottom: '1rem' }}>
										{query.description}
									</p>
								)}
							</div>
							<div
								style={{
									backgroundColor: '#f0f0f0',
									padding: '4px 12px',
									borderRadius: '4px',
									fontSize: '0.9em',
								}}
							>
								Позиция: {query.position}
							</div>
						</div>

						<div
							style={{
								display: 'flex',
								gap: '1rem',
								marginBottom: '1rem',
								flexWrap: 'wrap',
							}}
						>
							<div style={{ display: 'flex', flexDirection: 'column' }}>
								<strong style={{ fontSize: '0.9em', color: '#666' }}>
									Имя таблицы:
								</strong>
								<span style={{ marginTop: '0.25rem' }}>{query.tableName}</span>
							</div>
							<div style={{ display: 'flex', flexDirection: 'column' }}>
								<strong style={{ fontSize: '0.9em', color: '#666' }}>
									Подключение:
								</strong>
								<span style={{ marginTop: '0.25rem' }}>
									{getConnectionName(query.connectionId)}
								</span>
							</div>
						</div>

						<div
							className='query-sql'
							style={{
								backgroundColor: '#f8f9fa',
								padding: '1rem',
								borderRadius: '4px',
								fontFamily: 'monospace',
								whiteSpace: 'pre-wrap',
								wordBreak: 'break-all',
								margin: '1rem 0',
								fontSize: '0.9em',
								border: '1px solid #eaeaea',
							}}
						>
							{query.sql}
						</div>

						<div
							className='form-actions'
							style={{
								display: 'flex',
								gap: '0.75rem',
								marginTop: '1rem',
							}}
						>
							<button
								className='button button-small'
								onClick={() => handleEditQuery(query)}
								style={{ padding: '6px 12px', fontSize: '0.85em' }}
							>
								Редактировать запрос
							</button>
							<button
								className='button button-danger button-small'
								onClick={() => handleDeleteQuery(query.id)}
								style={{ padding: '6px 12px', fontSize: '0.85em' }}
							>
								Удалить запрос
							</button>
						</div>
					</div>
				))
			)}

			<div
				className='add-section'
				style={{
					display: 'flex',
					gap: '1rem',
					marginTop: '2rem',
					flexWrap: 'wrap',
				}}
			>
				<button className='button' onClick={handleAddQuery}>
					Добавить запрос
				</button>
				<button className='button' onClick={handleRunQueue}>
					Запустить очередь
				</button>
				<button className='button' onClick={() => setShowScheduleModal(true)}>
					Добавить расписание запуска очереди
				</button>
			</div>

			{/* Модальное окно добавления/редактирования запроса */}
			<Modal
				isOpen={showQueryModal}
				onClose={() => {
					setShowQueryModal(false)
					setEditingQuery(null)
				}}
				title={editingQuery ? 'Редактирование запроса' : 'Добавление запроса'}
				size='large'
			>
				<form onSubmit={handleSaveQuery}>
					<div className='form-group'>
						<label>Название запроса:</label>
						<input
							type='text'
							value={queryForm.name}
							onChange={e =>
								setQueryForm({ ...queryForm, name: e.target.value })
							}
							placeholder='Запрос №1'
							required
						/>
					</div>

					<div className='form-group'>
						<label>Подключение:</label>
						<select
							value={queryForm.connectionId}
							onChange={e =>
								setQueryForm({ ...queryForm, connectionId: e.target.value })
							}
							required
						>
							<option value=''>Выберите подключение</option>
							{connections.map(conn => (
								<option key={conn.id} value={conn.id}>
									{conn.name} ({conn.db_type})
								</option>
							))}
						</select>
					</div>

					<div className='form-group'>
						<label>SQL запрос:</label>
						<textarea
							rows='8'
							value={queryForm.sql}
							onChange={e =>
								setQueryForm({ ...queryForm, sql: e.target.value })
							}
							placeholder='SELECT * FROM ...'
							required
							style={{ fontFamily: 'monospace' }}
						/>
					</div>

					<div className='form-group'>
						<label>Имя таблицы:</label>
						<input
							type='text'
							value={queryForm.tableName}
							onChange={e =>
								setQueryForm({ ...queryForm, tableName: e.target.value })
							}
							placeholder='last_seen'
							required
						/>
					</div>

					<div className='form-group'>
						<label>Номер в очереди:</label>
						<select
							value={queryForm.position}
							onChange={e =>
								setQueryForm({
									...queryForm,
									position: parseInt(e.target.value),
								})
							}
							required
						>
							{[...Array(queries.length + 1).keys()].map(n => (
								<option key={n + 1} value={n + 1}>
									{n + 1}
								</option>
							))}
						</select>
					</div>

					<div className='form-group'>
						<label>Описание (необязательно):</label>
						<textarea
							rows='3'
							value={queryForm.description}
							onChange={e =>
								setQueryForm({ ...queryForm, description: e.target.value })
							}
							placeholder='Описание запроса...'
						/>
					</div>

					<div className='form-actions'>
						<button
							type='button'
							className='button button-secondary'
							onClick={() => {
								setShowQueryModal(false)
								setEditingQuery(null)
							}}
						>
							Отмена
						</button>
						<button type='submit' className='button'>
							{editingQuery ? 'Сохранить' : 'Добавить'}
						</button>
					</div>
				</form>
			</Modal>

			{/* Модальное окно расписания */}
			<Modal
				isOpen={showScheduleModal}
				onClose={() => setShowScheduleModal(false)}
				title='Расписание запуска очереди'
			>
				<form onSubmit={handleAddSchedule}>
					<div className='form-group'>
						<label>Интервал:</label>
						<select
							value={scheduleForm.intervalType}
							onChange={e =>
								setScheduleForm({
									...scheduleForm,
									intervalType: e.target.value,
								})
							}
							required
						>
							<option value='minute'>Минуты</option>
							<option value='hour'>Часы</option>
							<option value='day'>Дни</option>
							<option value='week'>Недели</option>
							<option value='month'>Месяцы</option>
						</select>
					</div>

					<div className='form-group'>
						<label>Значение интервала:</label>
						<input
							type='number'
							min='1'
							value={scheduleForm.intervalValue}
							onChange={e =>
								setScheduleForm({
									...scheduleForm,
									intervalValue: parseInt(e.target.value),
								})
							}
							placeholder='1'
							required
						/>
					</div>

					<div className='form-group'>
						<label>Начало выполнения:</label>
						<input
							type='datetime-local'
							value={scheduleForm.startAt}
							onChange={e =>
								setScheduleForm({ ...scheduleForm, startAt: e.target.value })
							}
						/>
						<small
							style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}
						>
							Если не указано, будет использовано текущее время
						</small>
					</div>

					<div className='form-group'>
						<label
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '10px',
								cursor: 'pointer',
							}}
						>
							<input
								type='checkbox'
								checked={scheduleForm.pushNow}
								onChange={e =>
									setScheduleForm({
										...scheduleForm,
										pushNow: e.target.checked,
									})
								}
							/>
							<span style={{ fontWeight: '500' }}>Запустить немедленно</span>
						</label>
					</div>

					<div className='form-actions'>
						<button
							type='button'
							className='button button-secondary'
							onClick={() => setShowScheduleModal(false)}
						>
							Отмена
						</button>
						<button type='submit' className='button'>
							Добавить расписание
						</button>
					</div>
				</form>
			</Modal>
		</div>
	)
}

export default QueueDetail
